import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Rating,
  useTheme,
} from '@mui/material';

interface TestimonialCardProps {
  name: string;
  role: string;
  location: string;
  rating: number;
  text: string;
  avatar?: string;
  service?: string;
}

export const TestimonialCard: React.FC<TestimonialCardProps> = ({
  name,
  role,
  location,
  rating,
  text,
  avatar,
  service,
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {/* Rating */}
        <Box sx={{ mb: 2 }}>
          <Rating value={rating} readOnly size="small" />
        </Box>

        {/* Testimonial Text */}
        <Typography
          variant="body1"
          sx={{
            fontStyle: 'italic',
            color: theme.palette.text.primary,
            lineHeight: 1.6,
            mb: 3,
            '&::before': {
              content: '"',
              fontSize: '2rem',
              color: theme.palette.primary.main,
              fontWeight: 'bold',
            },
            '&::after': {
              content: '"',
              fontSize: '2rem',
              color: theme.palette.primary.main,
              fontWeight: 'bold',
            },
          }}
        >
          {text}
        </Typography>

        {/* Service Badge */}
        {service && (
          <Box
            sx={{
              display: 'inline-block',
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.contrastText,
              px: 2,
              py: 0.5,
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              {service}
            </Typography>
          </Box>
        )}

        {/* Author Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={avatar}
            alt={name}
            sx={{
              width: 48,
              height: 48,
              backgroundColor: theme.palette.primary.main,
            }}
          >
            {name.charAt(0)}
          </Avatar>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.text.primary,
              }}
            >
              {name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                display: 'block',
              }}
            >
              {role}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                display: 'block',
              }}
            >
              {location}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
